import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../redux/authSlice";
import { logger } from "../utils/logger.js";
import { ClipLoader } from "react-spinners";
import { getMessages } from "../redux/messageSlice";

const AdminSidenav = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const messages = useSelector((state) => state.message);

  logger.debug("location", location.pathname);

  useEffect(() => {
    dispatch(getMessages());
  }, [dispatch]);

  const handleLogout = async () => {
    setLoading(true);
    try {
      // Timeout for visual feedback
      await new Promise((resolve) => {
        setTimeout(async () => {
          try {
            await dispatch(logout()).unwrap();
            resolve();
          } catch (error) {
            logger.error("Logout failed inner", error?.message);
            setLoading(false);
          }
        }, 1000);
      });
      navigate("/");
    } catch (error) {
      logger.error("Logout failed outer", error?.message);
      setLoading(false);
    }
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Update CSS variable when sidebar state changes
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--sidebar-width",
      isCollapsed ? "64px" : "256px"
    );
  }, [isCollapsed]);

  if (loading) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50"
        style={{ minHeight: "100vh" }}
      >
        <div className="flex flex-col items-center">
          <ClipLoader color="#38bdf8" size={60} />
          <div className="mt-4 text-white text-lg">Logging out...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <aside
        id="default-sidebar"
        className={`fixed top-0 left-0 z-40 h-screen transition-all duration-300 -translate-x-full sm:translate-x-0 ${
          isCollapsed ? "w-16" : "w-64"
        }`}
        aria-label="Sidebar"
      >
        <div className="h-full px-3 py-4 overflow-y-auto bg-gradient-to-b from-slate-950 via-slate-900/80 to-blue-950 ring-1 ring-blue-800/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] relative">
          {/* Toggle Button */}
          <button
            onClick={toggleSidebar}
            className={`absolute top-4 bg-sky-700/90 hover:bg-sky-600 text-white p-2 rounded-full transition-all duration-300 z-50 ring-1 ring-sky-300/30 shadow-[0_2px_10px_rgba(56,189,248,0.25)] focus:outline-none focus:ring-2 focus:ring-sky-400/40 ${
              isCollapsed ? "right-2" : "right-4"
            }`}
          >
            {isCollapsed ? (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            )}
          </button>

          <ul
            className={`space-y-2 font-medium ${
              isCollapsed ? "mt-16" : "mt-8"
            }`}
          >
            {!isCollapsed && (
              <h2 className="mt-5 text-md font-bold text-slate-300 py-2">
                Orders
              </h2>
            )}
            <hr className="border-gray-500 " />
            <li>
              <Link
                to="/open-orders"
                className={`flex items-center p-2 rounded-lg group transition-colors duration-150 ring-1 
                 ${
                   location.pathname === "/open-orders"
                     ? "bg-sky-400/15 ring-sky-400/30 text-sky-50"
                     : "hover:bg-white/5 hover:ring-sky-400/20 text-slate-200 ring-transparent"
                 }
                `}
                title={isCollapsed ? "Open Orders" : ""}
              >
                <svg
                  className={`w-6 h-6 transition duration-75 group-hover:text-sky-200 ${
                    location.pathname === "/open-orders"
                      ? "text-sky-300"
                      : "text-slate-400 "
                  }`}
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 4h1.5L9 16m0 0h8m-8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-8.5-3h9.25L19 7h-1M8 7h-.688M13 5v4m-2-2h4"
                  />
                </svg>

                {!isCollapsed && <span className="ms-3">Open Orders</span>}
              </Link>
            </li>
            <li>
              <Link
                to="/archived-orders"
                className={`flex items-center p-2 rounded-lg group transition-colors duration-150 ring-1 
                  ${
                    location.pathname === "/archived-orders"
                      ? "bg-sky-400/15 ring-sky-400/30 text-sky-50"
                      : "hover:bg-white/5 hover:ring-sky-400/20 text-slate-200 ring-transparent"
                  }
                 `}
                title={isCollapsed ? "Archived Orders" : ""}
              >
                <svg
                  className={`w-6 h-6 transition duration-75 group-hover:text-sky-200 ${
                    location.pathname === "/archived-orders"
                      ? "text-sky-300"
                      : "text-slate-400 "
                  }`}
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8.5 11.5 11 14l4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>

                {!isCollapsed && <span className="ms-3">Archived Orders</span>}
              </Link>
            </li>
            {!isCollapsed && (
              <h2 className="mt-5 text-md font-bold text-slate-300 py-2">
                Menu
              </h2>
            )}
            <hr className="border-gray-500 " />
            <li>
              <Link
                to="/pizza-builder"
                className={`flex items-center p-2 rounded-lg group transition-colors duration-150 ring-1 
                  ${
                    location.pathname === "/pizza-builder"
                      ? "bg-sky-400/15 ring-sky-400/30 text-sky-50"
                      : "hover:bg-white/5 hover:ring-sky-400/20 text-slate-200 ring-transparent"
                  }
                 `}
                title={isCollapsed ? "Add New Pizza" : ""}
              >
                <svg
                  className={`w-6 h-6 transition duration-75 group-hover:text-sky-200 ${
                    location.pathname === "/pizza-builder"
                      ? "text-sky-300"
                      : "text-slate-400 "
                  }`}
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 7.757v8.486M7.757 12h8.486M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>

                {!isCollapsed && (
                  <span className="flex-1 ms-3 whitespace-nowrap">
                    Add New Pizza
                  </span>
                )}
              </Link>
            </li>

            <li>
              <Link
                to="/menu-item-builder"
                className={`flex items-center p-2 rounded-lg group transition-colors duration-150 ring-1 
                  ${
                    location.pathname === "/menu-item-builder"
                      ? "bg-sky-400/15 ring-sky-400/30 text-sky-50"
                      : "hover:bg-white/5 hover:ring-sky-400/20 text-slate-200 ring-transparent"
                  }
                 `}
                title={isCollapsed ? "Add Menu Item" : ""}
              >
                <svg
                  className={`w-6 h-6 transition duration-75 group-hover:text-sky-200 ${
                    location.pathname === "/menu-item-builder"
                      ? "text-sky-300"
                      : "text-slate-400 "
                  }`}
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 2v20M2 12h20"
                  />
                </svg>

                {!isCollapsed && (
                  <span className="flex-1 ms-3 whitespace-nowrap">
                    Add Menu Item
                  </span>
                )}
              </Link>
            </li>

            <li>
              <Link
                to="/admin-menu"
                className={`flex items-center p-2 rounded-lg group transition-colors duration-150 ring-1 
                  ${
                    location.pathname === "/admin-menu"
                      ? "bg-sky-400/15 ring-sky-400/30 text-sky-50"
                      : "hover:bg-white/5 hover:ring-sky-400/20 text-slate-200 ring-transparent"
                  }
                 `}
                title={isCollapsed ? "Current Menu" : ""}
              >
                <svg
                  className={`w-6 h-6 transition duration-75 group-hover:text-sky-200 ${
                    location.pathname === "/admin-menu"
                      ? "text-sky-300"
                      : "text-slate-400 "
                  }`}
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <g clipPath="url(#a)">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m10.4149 10.7623.0005.0109m3.0868 3.0764.0005.0108M8.91554 15.349l.00046.0108m-.8276-8.44549L4.39857 19.9133l12.95163-3.7371m-.8271-8.43475c2.0971 2.09707 3.269 4.77055 3.5172 7.51635.067.7413-.4619 1.3752-1.1869 1.5293-1.0146.2158-1.9613-.5811-2.0926-1.615-.2412-1.9-.9437-3.5721-2.52-5.1484-1.5779-1.57793-3.3173-2.3457-5.25302-2.61955-1.02139-.1445-1.79555-1.1099-1.5387-2.10314.17236-.66653.76818-1.14208 1.45754-1.08543 2.78088.22851 5.49388 1.40332 7.61648 3.52587Z"
                    />
                  </g>
                  <defs>
                    <clipPath id="a">
                      <path fill="#fff" d="M0 0h24v24H0z" />
                    </clipPath>
                  </defs>
                </svg>

                {!isCollapsed && (
                  <span className="flex-1 ms-3 whitespace-nowrap">
                    Current Menu
                  </span>
                )}
              </Link>
            </li>
            <li>
              <Link
                to="/ingredient-table"
                className={`flex items-center p-2 rounded-lg group transition-colors duration-150 ring-1 
                  ${
                    location.pathname === "/ingredient-table"
                      ? "bg-sky-400/15 ring-sky-400/30 text-sky-50"
                      : "hover:bg-white/5 hover:ring-sky-400/20 text-slate-200 ring-transparent"
                  }`}
                title={isCollapsed ? "Ingredients Table" : ""}
              >
                <svg
                  className={`w-6 h-6 transition duration-75 group-hover:text-sky-200 ${
                    location.pathname === "/ingredient-table"
                      ? "text-sky-300"
                      : "text-slate-400 "
                  }`}
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6h8m-8 6h8m-8 6h8M4 16a2 2 0 1 1 3.321 1.5L4 20h5M4 5l2-1v6m-2 0h4"
                  />
                </svg>

                {!isCollapsed && (
                  <span className="flex-1 ms-3 whitespace-nowrap">
                    Ingredients Table
                  </span>
                )}
              </Link>
            </li>

            {!isCollapsed && (
              <h2 className="mt-5 text-md font-bold text-slate-300 py-2">
                Customer Feedback
              </h2>
            )}
            <hr className="border-gray-500 " />
            <li>
              <Link
                to="/admin-inbox"
                className={`flex items-center p-2 rounded-lg group transition-colors duration-150 ring-1 
                  ${
                    location.pathname === "/admin-inbox"
                      ? "bg-sky-400/15 ring-sky-400/30 text-sky-50"
                      : "hover:bg-white/5 hover:ring-sky-400/20 text-slate-200 ring-transparent"
                  }
                 `}
                title={isCollapsed ? "Inbox" : ""}
              >
                <svg
                  className={`w-6 h-6 transition duration-75 group-hover:text-sky-200 ${
                    location.pathname === "/admin-inbox"
                      ? "text-sky-300"
                      : "text-slate-400 "
                  }`}
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M17 6h-2V5h1a1 1 0 1 0 0-2h-2a1 1 0 0 0-1 1v2h-.541A5.965 5.965 0 0 1 14 10v4a1 1 0 1 1-2 0v-4c0-2.206-1.794-4-4-4-.075 0-.148.012-.22.028C7.686 6.022 7.596 6 7.5 6A4.505 4.505 0 0 0 3 10.5V16a1 1 0 0 0 1 1h7v3a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-3h5a1 1 0 0 0 1-1v-6c0-2.206-1.794-4-4-4Zm-9 8.5H7a1 1 0 1 1 0-2h1a1 1 0 1 1 0 2Z" />
                </svg>

                {!isCollapsed && (
                  <span className="flex-1 ms-3 whitespace-nowrap">Inbox</span>
                )}

                {!isCollapsed && (
                  <span
                    className={`inline-flex items-center justify-center w-4 h-4 p-3.5 text-sm font-bold rounded-full text-white bg-sky-700 border-2 border-sky-300 shadow-md animate-pulse ms-3`}
                  >
                    {messages?.messages?.length || 0}
                  </span>
                )}
              </Link>
            </li>
            {/* Layout section under Customer Feedback */}
            {!isCollapsed && (
              <h2 className="mt-5 text-md font-bold text-slate-300 py-2">
                Settings
              </h2>
            )}
            <hr className="border-gray-500 " />
            <li>
              <Link
                to="/settings"
                className={`flex items-center p-2 rounded-lg group transition-colors duration-150 ring-1 
                        ${
                          location.pathname === "/settings"
                            ? "bg-sky-400/15 ring-sky-400/30 text-sky-50"
                            : "hover:bg-white/5 hover:ring-sky-400/20 text-slate-200 ring-transparent"
                        }
                       `}
                title={isCollapsed ? "About page" : ""}
              >
                <svg
                  className={`w-6 h-6 transition duration-75 group-hover:text-sky-200 ${
                    location.pathname === "/settings"
                      ? "text-sky-300"
                      : "text-slate-400 "
                  }`}
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 19H5a1 1 0 0 1-1-1v-1a3 3 0 0 1 3-3h2m10 1a3 3 0 0 1-3 3m3-3a3 3 0 0 0-3-3m3 3h1m-4 3a3 3 0 0 1-3-3m3 3v1m-3-4a3 3 0 0 1 3-3m-3 3h-1m4-3v-1m-2.121 1.879-.707-.707m5.656 5.656-.707-.707m-4.242 0-.707.707m5.656-5.656-.707.707M12 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                </svg>

                {!isCollapsed && (
                  <span className="flex-1 ms-3 whitespace-nowrap">
                    About page
                  </span>
                )}
              </Link>
            </li>
            <li>
              <Link
                to="/settings/operating-hours"
                className={`flex items-center p-2 rounded-lg group transition-colors duration-150 ring-1 
                        ${
                          location.pathname === "/settings/operating-hours"
                            ? "bg-sky-400/15 ring-sky-400/30 text-sky-50"
                            : "hover:bg-white/5 hover:ring-sky-400/20 text-slate-200 ring-transparent"
                        }
                       `}
                title={isCollapsed ? "Operating Hours" : ""}
              >
                <svg
                  className={`w-6 h-6 transition duration-75 group-hover:text-sky-200 ${
                    location.pathname === "/settings/operating-hours"
                      ? "text-sky-300"
                      : "text-slate-400 "
                  }`}
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6v6l4 2m6-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>

                {!isCollapsed && (
                  <span className="flex-1 ms-3 whitespace-nowrap">
                    Operating Hours
                  </span>
                )}
              </Link>
            </li>
            {!isCollapsed && (
              <h2 className="mt-5 text-md font-bold text-slate-300 py-2">
                Your Account
              </h2>
            )}
            <hr className="border-gray-500 " />
            <li>
              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center p-2 rounded-lg group transition-colors duration-150 hover:bg-white/5 hover:ring-1 hover:ring-red-400/20 text-slate-200 w-full cursor-pointer"
                title={isCollapsed ? "Sign Out" : ""}
              >
                <svg
                  className="w-6 h-6 transition duration-75 group-hover:text-red-300 text-slate-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 8a4 4 0 1 1 8 0 4 4 0 0 1-8 0Zm-2 9a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1Zm13-6a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2h-4Z"
                    clipRule="evenodd"
                  />
                </svg>
                {!isCollapsed && (
                  <span className="flex ms-3 whitespace-nowrap">Sign Out</span>
                )}
              </button>
            </li>
            <li>
              <Link
                to="/admin/add-admin"
                className={`flex items-center p-2 rounded-lg group transition-colors duration-150 ring-1 
                        ${
                          location.pathname === "/admin/add-admin"
                            ? "bg-sky-400/15 ring-sky-400/30 text-sky-50"
                            : "hover:bg-white/5 hover:ring-sky-400/20 text-slate-200 ring-transparent"
                        }
                       `}
                title={isCollapsed ? "Add Admin" : ""}
              >
                <svg
                  className={`w-6 h-6 transition duration-75 group-hover:text-sky-200 ${
                    location.pathname === "/admin/add-admin"
                      ? "text-sky-300"
                      : "text-slate-400 "
                  }`}
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v12m6-6H6"
                  />
                </svg>

                {!isCollapsed && (
                  <span className="flex-1 ms-3 whitespace-nowrap">
                    Add Admin
                  </span>
                )}
              </Link>
            </li>
            <li>
              <Link
                to="/settings/password"
                className={`flex items-center p-2 rounded-lg group transition-colors duration-150 ring-1 
                        ${
                          location.pathname === "/settings/password"
                            ? "bg-sky-400/15 ring-sky-400/30 text-sky-50"
                            : "hover:bg-white/5 hover:ring-sky-400/20 text-slate-200 ring-transparent"
                        }
                       `}
                title={isCollapsed ? "Change Password" : ""}
              >
                <svg
                  className={`w-6 h-6 transition duration-75 group-hover:text-sky-200 ${
                    location.pathname === "/settings/password"
                      ? "text-sky-300"
                      : "text-slate-400 "
                  }`}
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16 11V8a4 4 0 1 0-8 0v3M7 11h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
                  />
                </svg>

                {!isCollapsed && (
                  <span className="flex-1 ms-3 whitespace-nowrap">
                    Change Password/Email
                  </span>
                )}
              </Link>
            </li>
          </ul>
        </div>
      </aside>
    </>
  );
};

export default AdminSidenav;
